<template>
  <div>
    <v-dialog
      v-model="dialog"
      persistent
      eager
      scrollable
      max-width="700px"
      content-class="rounded-dialog"
    >
      <v-form ref="PerformanceTarget" @submit.prevent>
        <v-card>
          <v-card-title dark class="dialog-header">
            <div class="dialog-header-content">
              <div class="dialog-title-wrapper">
                <div class="dialog-icon">
                  <icon name="circle-add" />
                </div>

                <div class="dialog-text">
                  <div class="dialog-title">
                    {{ action == "Add" ? "Add New IPCR" : action + " IPCR" }}
                  </div>

                  <div class="dialog-subtitle">
                    Configure performance target information
                  </div>
                </div>
              </div>

              <v-btn icon class="close-btn" @click="closeD()">
                <icon name="circle-close" />
              </v-btn>
            </div>
          </v-card-title>

          <v-card-text style="max-height: 700px">
            <v-container>
              <v-row>
                <!-- Teaching Personnel -->
                <v-col cols="12" class="field-spacing mt-4">
                  <div
                    class="metric-card"
                    :class="{ active: isFaculty }"
                    @click="action !== 'Update' && (isFaculty = !isFaculty)"
                  >
                    <v-switch
                      v-model="isFaculty"
                      hide-details
                      class="metric-checkbox"
                      color="#147452"
                      :disabled="action == 'Update'"
                      @click.stop
                    />

                    <div class="metric-content">
                      <div>
                        <div class="metric-title">Teaching Personnel</div>
                        <div class="metric-subtitle">
                          Enable faculty-specific IPCR settings
                        </div>
                      </div>
                    </div>
                  </div>
                </v-col>

                <!-- Faculty Type -->
                <v-col cols="12" class="field-spacing" v-if="isFaculty">
                  <v-row>
                    <v-col cols="12 mb-4 mt-3" md="6" class="field-spacing">
                      <div
                        class="metric-card"
                        :class="{ active: facultyType === 'designated' }"
                        @click="facultyType = 'designated'"
                      >
                        <div
                          class="custom-radio"
                          :class="{ active: facultyType === 'designated' }"
                        >
                          <div class="custom-radio-dot"></div>
                        </div>

                        <div class="metric-content">
                          <div>
                            <div class="metric-title">Designated</div>
                            <div class="metric-subtitle">
                              With administrative designation
                            </div>
                          </div>
                        </div>
                      </div>
                    </v-col>

                    <v-col cols="12" md="6" class="field-spacing mt-3">
                      <div
                        class="metric-card"
                        :class="{ active: facultyType === 'non-designated' }"
                        @click="
                          action !== 'Update' &&
                            (facultyType = 'non-designated')
                        "
                      >
                        <div
                          class="custom-radio"
                          :class="{ active: facultyType === 'non-designated' }"
                        >
                          <div class="custom-radio-dot"></div>
                        </div>

                        <div class="metric-content">
                          <div>
                            <div class="metric-title">Non-Designated</div>
                            <div class="metric-subtitle">
                              Teaching personnel only
                            </div>
                          </div>
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                </v-col>

                <!-- Designation -->
                <v-col
                  cols="12"
                  class="field-spacing"
                  v-if="isFaculty && facultyType == 'designated'"
                >
                  <v-autocomplete
                    v-model="designationID"
                    :rules="
                      isFaculty && facultyType == 'designated'
                        ? [formRules.required]
                        : []
                    "
                    outlined
                    class="rounded-lg"
                    item-text="description"
                    item-value="id"
                    label="Designation"
                    color="#93CB5B"
                    :items="designationList"
                  />
                </v-col>

                <!-- Faculty Rank -->
                <v-col cols="12" class="field-spacing" v-if="isFaculty">
                  <v-select
                    label="Faculty Rank"
                    v-model="facultyRank"
                    :items="facultyRankList"
                    :rules="[formRules.required]"
                    outlined
                  />
                </v-col>

                <!-- Year -->
                <v-col cols="12" class="field-spacing">
                  <v-select
                    label="Year"
                    outlined
                    v-model="year"
                    :rules="[formRules.required]"
                    class="rounded-lg"
                    color="#6DB249"
                    :items="yearList"
                  />
                </v-col>

                <!-- Period -->
                <v-col cols="12" class="field-spacing">
                  <v-row>
                    <v-col cols="12" sm="6" class="field-spacing">
                      <v-autocomplete
                        v-model="periodFrom"
                        :rules="[formRules.required]"
                        outlined
                        label="Period (From)"
                        item-text="mnth"
                        item-value="val"
                        :items="periodList"
                      />
                    </v-col>

                    <v-col cols="12" sm="6" class="field-spacing">
                      <v-autocomplete
                        v-model="periodTo"
                        :rules="[formRules.required]"
                        outlined
                        label="Period (To)"
                        item-text="mnth"
                        item-value="val"
                        :items="getRemainingPeriodList"
                      />
                    </v-col>
                  </v-row>
                </v-col>

                <!-- Assessed By -->
                <v-col cols="12" class="field-spacing">
                  <v-autocomplete
                    label="Assessed By"
                    v-model="assessed_by_user"
                    :rules="[formRules.required]"
                    :readonly="action == 'View'"
                    outlined
                    class="rounded-lg"
                    item-text="name"
                    item-value="user_detailID"
                    color="#93CB5B"
                    :items="recommendingUsersList"
                  />
                </v-col>

                <!-- Final Rating By -->
                <v-col cols="12" class="field-spacing">
                  <v-autocomplete
                    label="Final Rating By"
                    v-model="final_rating_by_user"
                    :rules="[formRules.required]"
                    :readonly="action == 'View'"
                    outlined
                    class="rounded-lg"
                    item-text="name"
                    item-value="id"
                    color="#93CB5B"
                    :items="usersList"
                  />
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-divider></v-divider>

          <v-card-actions class="modern-actions">
            <v-spacer></v-spacer>

            <v-btn class="btn-cancel" outlined @click="closeD()">
              Cancel
            </v-btn>

            <v-btn
              v-if="action == 'Add'"
              class="btn-save"
              :loading="btnLoading"
              :disabled="btnLoading"
              @click="addnew()"
            >
              Add IPCR
            </v-btn>

            <v-btn
              v-if="action == 'Update'"
              class="btn-save"
              :loading="btnLoading"
              :disabled="btnLoading"
              @click="update()"
            >
              Update IPCR
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>

    <fade-away-message-component
      displayType="variation2"
      v-model="fadeAwayMessage.show"
      :message="fadeAwayMessage.message"
      :header="fadeAwayMessage.header"
      :top="fadeAwayMessage.top"
      :type="fadeAwayMessage.type"
      :timeout="3000"
    ></fade-away-message-component>
  </div>
</template>

<script>
import icon from "@/assets/icon.vue";
export default {
  components: {
    icon,
  },
  props: {
    data: null,
    action: null,
  },

  data() {
    return {
      id: null,
      dialog: false,
      fadeAwayMessage: {
        show: false,
        type: "success",
        header: "Successfully Added!",
        message: "",
        top: 10,
      },
      yearList: [],
      period: null,
      assessed_by_user: null,
      final_rating_by_user: null,
      btnLoading: false,
      periodFrom: null,
      periodTo: null,
      periodList: [
        {
          val: "1",
          mnth: "January",
        },
        {
          val: "2",
          mnth: "February",
        },
        {
          val: "3",
          mnth: "March",
        },
        {
          val: "4",
          mnth: "April",
        },
        {
          val: "5",
          mnth: "May",
        },
        {
          val: "6",
          mnth: "June",
        },
        {
          val: "7",
          mnth: "July",
        },
        {
          val: "8",
          mnth: "August",
        },
        {
          val: "9",
          mnth: "September",
        },
        {
          val: "10",
          mnth: "October",
        },
        {
          val: "11",
          mnth: "November",
        },
        {
          val: "12",
          mnth: "December",
        },
      ],
      year: null,
      presidentUser: null,
      isHeadPresident: null,
      usersList: [],
      usersHeadProgAbove: [],
      recommendingUsersList: [],
      isFaculty: null,
      facultyUnits: null,
      facultyDeloadedUnits: null,
      facultyType: "non-designated",
      facultyRank: null,
      facultyRankList: [
        "Instructor",
        "Associate Professor",
        "Professor",
        "College Professor",
      ],
      designationList: [],
      designationID: null,
      facultyStrategicIndicatorList: [
        {
          key: "Instructor",
          indicator_count: 4,
        },
        {
          key: "Assistant Professor",
          indicator_count: 5,
        },
        {
          key: "Associate Professor",
          indicator_count: 6,
        },
        {
          key: "Professor",
          indicator_count: 7,
        },
        {
          key: "College Professor",
          indicator_count: 7,
        },
      ],
      facultyStrategicIndicator: null,
    };
  },
  watch: {
    data: {
      handler(data) {
        this.dialog = true;
        this.loadYear();
        this.getDesignations();
        this.loadRecommendingOfficers();
        this.getUsers();
        this.getHeadProgChairAboveUsers();
        if (data.id) {
          console.table("IPCR DATA", data);
          this.periodFrom = data.periodFrom.toString();
          this.periodTo = data.periodTo.toString();
          this.id = data.id;
          this.year = Number(data.year);
          this.isFaculty = data.isFaculty;
          this.facultyRank = data.facultyRank;
          this.designationID = data.designationID;
          this.facultyType = data.facultyType;
          this.assessed_by_user = Number(data.assesed_by);
          this.final_rating_by_user = data.final_rating_by.toString();
        } else {
          this.periodFrom = null;
          this.periodTo = null;
          this.id = null;
          this.year = null;
        }
      },

      deep: true,
    },
  },

  computed: {
    getRemainingPeriodList() {
      let rem = [];
      if (this.periodFrom) {
        rem = this.periodList.slice(this.periodFrom);
      }
      return rem;
    },

    getUnitsDeload() {
      let deload = 0;
      for (let i = 0; i < this.designationList.length; i++) {
        if (this.designationID == this.designationList[i].id) {
          deload = this.designationList[i].units_deload;
        }
      }
      return deload;
    },
  },

  methods: {
    initialize() {},
    loadRecommendingOfficers() {
      this.axiosCall("/my-direct-head/loadRecommendingOfficers", "GET").then(
        (res) => {
          if (res.data) {
            console.log("recommendingListsss", res.data);
            this.presidentUser = res.data.pres_emp;
            let data = res.data.heads;
            data.forEach((element, i) => {
              data[i].name = this.toTitleCase(element.name);
            });
            this.recommendingUsersList = data;

            this.recommending_userID = res.data.heads[0].user_detailID;
            this.checkIfHeadIsPresident();
          }
        },
      );
    },

    checkIfHeadIsPresident() {
      if (this.presidentUser.user_detailID && this.recommending_userID) {
        if (this.presidentUser.user_detailID == this.recommending_userID) {
          this.isHeadPresident = true;
        } else {
          this.isHeadPresident = false;
        }
      }
    },

    getUsers() {
      this.axiosCall("/user-details/getDeanDirAboveUsers", "GET").then(
        (res) => {
          let data = res.data;
          data.forEach((element, i) => {
            data[i].name = this.toTitleCase(element.name);
          });
          this.usersList = data;
          // console.log(res.data);
        },
      );
    },

    getHeadProgChairAboveUsers() {
      this.axiosCall(
        "/user-details/getUsersHeadProgChairAbove_Office",
        "GET",
      ).then((res) => {
        let data = res.data;
        data.forEach((element, i) => {
          data[i].name = this.toTitleCase(element.name);
        });
        this.usersHeadProgAbove = data;
        // console.log(res.data);
      });
    },

    getDesignations() {
      this.axiosCall("/designations", "GET").then((res) => {
        let data = res.data;
        this.designationList = data;
      });
    },

    loadYear() {
      let d = new Date();

      let yr = d.getFullYear();

      for (let i = yr; i >= 2020; i--) {
        this.yearList.push(i);
      }
    },
    closeD() {
      this.eventHub.$emit("closePerformanceEvaluationDialog", false);
      this.btnLoading = false;
      this.dialog = false;
    },

    addnew() {
      this.btnLoading = true;
      if (this.$refs.PerformanceTarget.validate()) {
        let facultyStrategicIndicatorKey = null;
        if (this.isFaculty) {
          facultyStrategicIndicatorKey = this.facultyStrategicIndicatorList.findIndex(
            (list) => list.key === this.facultyRank,
          );
        }

        let data = {
          isFaculty: this.isFaculty ? true : false,
          facultyDeloadedUnits:
            this.isFaculty && this.facultyType == "designated"
              ? this.getUnitsDeload
              : null,
          facultyUnits: this.isFaculty ? 18 : null,
          facultyRank: this.isFaculty ? this.facultyRank : null,
          facultyStrategicIndicator: this.isFaculty
            ? this.facultyStrategicIndicatorList[facultyStrategicIndicatorKey]
                .indicator_count
            : null,
          facultyType: this.isFaculty ? this.facultyType : null,
          designationID: this.designationID,
          periodFrom: this.periodFrom,
          periodTo: this.periodTo,
          year: this.year,
          assessed_by_user: this.assessed_by_user,
          final_rating_by_user: this.final_rating_by_user,
        };

        this.axiosCall("/ipcr", "POST", data).then((res) => {
          if (res.data.status == 201) {
            this.fadeAwayMessage.show = true;
            this.fadeAwayMessage.type = "success";
            this.fadeAwayMessage.header = "System Message";
            this.fadeAwayMessage.message = res.data.msg;
            this.btnLoading = false;
            this.closeD();
          } else {
            this.btnLoading = false;
            this.fadeAwayMessage.show = true;
            this.fadeAwayMessage.type = "error";
            this.fadeAwayMessage.header = "System Message";
            this.fadeAwayMessage.message = res.data.msg;
          }
        });
      } else {
        this.btnLoading = false;
      }
    },

    update() {
      this.btnLoading = true;
      if (this.$refs.PerformanceTarget.validate()) {
        let facultyStrategicIndicatorKey = null;
        if (this.isFaculty) {
          facultyStrategicIndicatorKey = this.facultyStrategicIndicatorList.findIndex(
            (list) => list.key === this.facultyRank,
          );
        }
        let data = {
          isFaculty: this.isFaculty ? true : false,
          facultyDeloadedUnits:
            this.isFaculty && this.facultyType == "designated"
              ? this.getUnitsDeload
              : null,
          facultyUnits: this.isFaculty ? 18 : null,
          facultyRank: this.isFaculty ? this.facultyRank : null,
          facultyStrategicIndicator: this.isFaculty
            ? this.facultyStrategicIndicatorList[facultyStrategicIndicatorKey]
                .indicator_count
            : null,
          facultyType: this.isFaculty ? this.facultyType : null,
          designationID: this.designationID,
          periodFrom: this.periodFrom,
          periodTo: this.periodTo,
          year: this.year,
          assessed_by_user: this.assessed_by_user,
          final_rating_by_user: this.final_rating_by_user,
        };

        this.axiosCall("/ipcr/" + this.id, "PATCH", data).then((res) => {
          if (res.data.status == 200) {
            this.fadeAwayMessage.show = true;
            this.fadeAwayMessage.type = "success";
            this.fadeAwayMessage.header = "System Message";
            this.fadeAwayMessage.message = res.data.msg;
            this.btnLoading = false;
            this.closeD();
          } else {
            this.btnLoading = false;
            this.fadeAwayMessage.show = true;
            this.fadeAwayMessage.type = "error";
            this.fadeAwayMessage.header = "System Message";
            this.fadeAwayMessage.message = res.data.msg;
          }
        });
      } else {
        this.btnLoading = false;
      }
    },
  },
};
</script>
